import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { ActivityIndicator, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'

import { Row, SortingState } from '@tanstack/react-table'
import { uniqueId } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getDocumentUploadErrorMessage } from '@lyrahealth-inc/shared-app-logic/src/features/documents/documentErrorUtils'
import {
  DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH,
  DOCUMENT_UPLOAD_MAX_FILE_SIZE,
} from '@lyrahealth-inc/shared-app-logic/src/features/documents/documentValidation'
import { PROGRAMS } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { DocumentType as APIDocumentType } from '@lyrahealth-inc/shared-app-logic/src/models/document/Document'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { FileUpload } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/FileUpload'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { DownloadFileIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/DownloadFileIcon'
import { EditIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/EditIcon'
import { ExternalLinkIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExternalLinkIcon'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { TrashIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TrashIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { Image } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/image/Image'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { UnstyledIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/unstyledIconButton/UnstyledIconButton'
import { DataTable } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTable'
import { DocumentFilters } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/documentFilters/DocumentFilters'
import {
  DocumentType,
  FilterSectionConfig,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/documentFilters/DocumentFiltersPopover'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  useDeleteDocumentTemplateMutation,
  useDownloadDocumentTemplateMutation,
  useGetDocumentTemplatesQuery,
  useLazyGetTemplateEditorUrlQuery,
  useUpdateDocumentTemplateMutation,
  useUploadDocumentTemplateMutation,
} from './data/templatesApi'
import useDocumentsColumns from './hooks/useDocumentsColumns'
import { DA_ROLES } from '../../common/constants/appConstants'
import { getAuthIsLTSupervisor, getAuthRoles, getAuthUserId } from '../../data/auth/authSelectors'
import { DocuSignEmbeddedView, DocuSignEvent } from '../clientDocuments/DocuSignEmbeddedView'
import { setToastContent } from '../data/ltToastAutoActions'

type DocumentsPageFilterState = {
  type: DocumentType[]
  autoSend: string[]
}

const INITIAL_DOCUMENTS_PAGE_FILTER_STATE: DocumentsPageFilterState = {
  type: [],
  autoSend: [],
}

export type LibraryDocumentItem = {
  id: string
  fileName: string
  type: DocumentType
  uploadedDate: string
  autoSend: string[]
  signingFlow?: string
}

const UI_TO_API_DOCUMENT_TYPE: Record<DocumentType, APIDocumentType> = {
  [DocumentType.INTAKE_FORM]: APIDocumentType.INTAKE_FORM,
  [DocumentType.ASSESSMENT]: APIDocumentType.ASSESSMENT,
  [DocumentType.ACTIVITY]: APIDocumentType.ACTIVITY,
  [DocumentType.OTHER]: APIDocumentType.OTHER,
}

const API_TO_UI_DOCUMENT_TYPE: Record<APIDocumentType, DocumentType> = {
  [APIDocumentType.INTAKE_FORM]: DocumentType.INTAKE_FORM,
  [APIDocumentType.ASSESSMENT]: DocumentType.ASSESSMENT,
  [APIDocumentType.ACTIVITY]: DocumentType.ACTIVITY,
  [APIDocumentType.OTHER]: DocumentType.OTHER,
}

const OuterContainer = styled.View({
  flexDirection: 'row',
  flex: 1,
  overflow: 'hidden',
})

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  padding: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
  gap: theme.spacing['16px'],
}))

const FilterRowWrapper = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 10,
})

const FilterRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

const SearchIconWrapper = styled.View({
  width: 24,
  height: 24,
  justifyContent: 'center',
  alignItems: 'center',
})

const RowActionsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['12px'],
  padding: theme.spacing['8px'],
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: 58,
  backgroundColor: theme.colors.backgroundPrimary,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
}))

const ActionIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['4px'],
}))

const EmptyTableContainer = styled.View<{ theme: ThemeType }>(({ theme }: { theme: ThemeType }) => ({
  margin: theme.spacing['48px'],
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const EmptyTableText = styled(BodyText)({
  textAlign: 'center',
})

const EmptyTableButtonWrapper = styled.View({
  alignSelf: 'center',
})

const UploadModalContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.backgroundPrimary,
  display: 'flex',
  flexDirection: 'column',
}))

const UploadModalHeader = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing['24px'],
}))

const UploadModalHeaderButtons = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const UploadModalContent = styled.View({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
})

const UploadTabContent = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  padding: theme.spacing['24px'],
}))

const UploadTabSplitContainer = styled.View({
  flex: 1,
  flexDirection: 'row',
})

const UploadTabLeftPane = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  paddingRight: theme.spacing['24px'],
}))

const UploadTabRightPane = styled.View({
  flex: 1,
})

const FilePreviewContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.colors.borderDefault,
  borderRadius: 8,
  backgroundColor: theme.colors.backgroundSecondary,
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}))

const FilePreviewEmptyState = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing['24px'],
}))

const AutoSendSection = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const AutoSendHeaderRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
})

const AutoSendHeaderBlock = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['4px'],
}))

const EditModalContent = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['24px'],
  gap: theme.spacing['16px'],
}))

const EditModalHeader = styled.View(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const EditModalButtonRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: theme.spacing['8px'],
  marginTop: theme.spacing['8px'],
}))

const AUTO_SEND_MESSAGES = defineMessages({
  individualTherapy: { defaultMessage: 'Individual Therapy', description: 'Auto send option' },
  familyTherapy: { defaultMessage: 'Family Therapy', description: 'Auto send option' },
  couplesTherapy: { defaultMessage: 'Couples Therapy', description: 'Auto send option' },
  adolescentTherapy: { defaultMessage: 'Adolescent Therapy (13-17 years old)', description: 'Auto send option' },
  childTherapy: { defaultMessage: 'Child Therapy (0-12 years old)', description: 'Auto send option' },
})

const DOCUSIGN_TOAST_MESSAGES = defineMessages({
  templateCreated: {
    defaultMessage: 'Template {fileName} created successfully',
    description: 'Toast message after successful DocuSign template creation',
  },
  templateUpdated: {
    defaultMessage: 'Template {fileName} updated',
    description: 'Toast message after successfully updating a DocuSign draft template',
  },
})

const AUTO_SEND_OPTIONS: Array<{ value: string; key: keyof typeof AUTO_SEND_MESSAGES }> = [
  { value: PROGRAMS.DirectAccessIndividualTherapy.blendedCareProgramId, key: 'individualTherapy' },
  { value: PROGRAMS.DirectAccessFamilyTherapy.blendedCareProgramId, key: 'familyTherapy' },
  { value: PROGRAMS.DirectAccessCouplesTherapy.blendedCareProgramId, key: 'couplesTherapy' },
  { value: PROGRAMS.DirectAccessIndividualAdolescentTherapy.blendedCareProgramId, key: 'adolescentTherapy' },
  { value: PROGRAMS.DirectAccessIndividualChildTherapy.blendedCareProgramId, key: 'childTherapy' },
]

const DocumentsPage: React.FC = () => {
  const intl = useIntl()
  const { colors } = useTheme()
  const dispatch = useDispatch()

  const authRoles = useSelector(getAuthRoles)
  const isDaProvider = DA_ROLES.some((role) => authRoles.includes(role))

  const [searchTerm, setSearchTerm] = useState('')
  const [filterState, setFilterState] = useState<DocumentsPageFilterState>(INITIAL_DOCUMENTS_PAGE_FILTER_STATE)
  const [sortingState, setSortingState] = useState<SortingState>([{ id: 'uploadedDate', desc: true }])

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadFileName, setUploadFileName] = useState('')
  const [uploadDocumentType, setUploadDocumentType] = useState<DocumentType | ''>(
    isDaProvider ? DocumentType.INTAKE_FORM : DocumentType.ASSESSMENT,
  )
  const [uploadSignatureRequired, setUploadSignatureRequired] = useState('')
  const [autoSendSettings, setAutoSendSettings] = useState<string[]>([])
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | undefined>(undefined)
  const [documentToDelete, setDocumentToDelete] = useState<LibraryDocumentItem | null>(null)
  const [documentToEdit, setDocumentToEdit] = useState<LibraryDocumentItem | null>(null)
  const [editFileName, setEditFileName] = useState('')
  const [editDocumentType, setEditDocumentType] = useState<DocumentType | ''>(
    isDaProvider ? DocumentType.INTAKE_FORM : DocumentType.ASSESSMENT,
  )
  const [editAutoSend, setEditAutoSend] = useState<string[]>([])
  const [isDocuSignModalOpen, setIsDocuSignModalOpen] = useState(false)
  const [docuSignUrl, setDocuSignUrl] = useState<string | null>(null)
  const [isReopeningDraft, setIsReopeningDraft] = useState(false)
  const docuSignDocumentIdRef = useRef<string | null>(null)
  const docuSignDocumentNameRef = useRef<string | null>(null)
  const isLTSupervisor = useSelector(getAuthIsLTSupervisor)
  const userId = useSelector(getAuthUserId)
  const supervisorProviderId = isLTSupervisor ? userId : undefined

  const { data: templatesData, isLoading: isTemplatesLoading } = useGetDocumentTemplatesQuery(
    supervisorProviderId ? { providerId: supervisorProviderId } : undefined,
  )
  const [triggerGetTemplateEditorUrl] = useLazyGetTemplateEditorUrlQuery()
  const [uploadTemplate, { isLoading: isUploading }] = useUploadDocumentTemplateMutation()
  const [downloadTemplate] = useDownloadDocumentTemplateMutation()
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateDocumentTemplateMutation()
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteDocumentTemplateMutation()

  const documents: LibraryDocumentItem[] = useMemo(
    () =>
      (templatesData?.document_templates ?? []).map((template) => ({
        id: template.document_template_id,
        fileName: template.file_name,
        type: API_TO_UI_DOCUMENT_TYPE[template.document_type] ?? DocumentType.OTHER,
        uploadedDate: template.create_date,
        autoSend: template.autosend_program_ids ?? [],
        signingFlow: template.signing_flow,
      })),
    [templatesData],
  )

  const documentTypeOptions = [
    ...(isDaProvider ? [{ label: DocumentType.INTAKE_FORM, value: DocumentType.INTAKE_FORM }] : []),
    { label: DocumentType.ASSESSMENT, value: DocumentType.ASSESSMENT },
    { label: DocumentType.ACTIVITY, value: DocumentType.ACTIVITY },
    { label: DocumentType.OTHER, value: DocumentType.OTHER },
  ]

  const signatureRequiredOptions = [
    {
      label: intl.formatMessage({ defaultMessage: 'No signature required', description: 'Signature option' }),
      value: 'none',
    },
    {
      label: intl.formatMessage({ defaultMessage: 'From client only', description: 'Signature option' }),
      value: 'client',
    },
    {
      label: intl.formatMessage({ defaultMessage: 'From client and provider', description: 'Signature option' }),
      value: 'both',
    },
  ]

  useEffect(() => {
    if (!uploadFile) {
      setFilePreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(uploadFile)
    setFilePreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [uploadFile])

  const isImageFile = (file: File): boolean => file.type.startsWith('image/')
  const isPdfFile = (file: File): boolean => file.type === 'application/pdf'

  const handleUploadModalOpen = () => setIsUploadModalOpen(true)

  const handleUploadModalClose = useCallback(() => {
    setIsUploadModalOpen(false)
    setUploadFile(null)
    setUploadFileName('')
    setUploadDocumentType(isDaProvider ? DocumentType.INTAKE_FORM : DocumentType.ASSESSMENT)
    setUploadSignatureRequired('')
    setAutoSendSettings([])
    setUploadErrorMessage(undefined)
    setDocuSignUrl(null)
    docuSignDocumentIdRef.current = null
    docuSignDocumentNameRef.current = null
  }, [isDaProvider])

  const toggleAutoSend = (option: string) => {
    setAutoSendSettings((prev) => (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]))
  }

  const signingFlowMap: Record<string, string> = {
    client: 'PATIENT_ONLY',
    both: 'PATIENT_THEN_PROVIDER',
  }

  const handleUploadConfirm = async () => {
    if (!uploadFile || !uploadFileName || !uploadDocumentType) return
    try {
      const result = await uploadTemplate({
        file: uploadFile,
        fileName: uploadFileName,
        documentType: UI_TO_API_DOCUMENT_TYPE[uploadDocumentType],
        autoSend: autoSendSettings,
        signingFlow:
          uploadSignatureRequired && uploadSignatureRequired !== 'none'
            ? signingFlowMap[uploadSignatureRequired]
            : undefined,
        providerId: supervisorProviderId,
      }).unwrap()

      if (uploadSignatureRequired && uploadSignatureRequired !== 'none') {
        const returnUrl = `${window.location.origin}/docusign-complete.html`
        const urlResult = await triggerGetTemplateEditorUrl({
          templateId: result.document_template_id,
          returnUrl,
          providerId: supervisorProviderId,
        }).unwrap()

        if (urlResult.editor_url) {
          docuSignDocumentIdRef.current = result.document_template_id
          docuSignDocumentNameRef.current = uploadFileName
          setDocuSignUrl(urlResult.editor_url)
          setIsUploadModalOpen(false)
          setIsDocuSignModalOpen(true)
        } else {
          handleUploadModalClose()
          dispatch(
            setToastContent({
              text: intl.formatMessage({
                defaultMessage: 'Could not open DocuSign editor. Please try again.',
                description: 'Error message when DocuSign editor URL is missing after upload',
              }),
              id: uniqueId('template-editor-url-missing'),
              toastType: 'error',
            }),
          )
        }
      } else {
        handleUploadModalClose()
      }
    } catch (error) {
      setUploadErrorMessage(getDocumentUploadErrorMessage(error, intl))
    }
  }

  const handleOpenDraftInDocuSign = useCallback(
    async (doc: LibraryDocumentItem) => {
      const returnUrl = `${window.location.origin}/docusign-complete.html`
      setDocuSignUrl(null)
      docuSignDocumentIdRef.current = doc.id
      docuSignDocumentNameRef.current = doc.fileName
      setIsReopeningDraft(true)
      setIsDocuSignModalOpen(true)
      try {
        const urlResult = await triggerGetTemplateEditorUrl({
          templateId: doc.id,
          returnUrl,
          providerId: supervisorProviderId,
        }).unwrap()
        if (urlResult.editor_url) {
          setDocuSignUrl(urlResult.editor_url)
          return
        }
        throw new Error('No editor URL returned')
      } catch {
        setIsDocuSignModalOpen(false)
        setIsReopeningDraft(false)
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: 'Failed to open draft in DocuSign. Please try again.',
              description: 'Error message when fetching draft template editor URL fails',
            }),
            id: uniqueId('docusign-draft-open-error'),
            toastType: 'error',
          }),
        )
      }
    },
    [dispatch, intl, supervisorProviderId, triggerGetTemplateEditorUrl],
  )

  const handleDocuSignComplete = useCallback(
    async (event: DocuSignEvent) => {
      const templateId = docuSignDocumentIdRef.current
      const templateName = docuSignDocumentNameRef.current
      const wasReopeningDraft = isReopeningDraft
      setIsDocuSignModalOpen(false)
      setIsReopeningDraft(false)
      handleUploadModalClose()
      if (event === 'save') {
        dispatch(
          setToastContent({
            text: intl.formatMessage(
              wasReopeningDraft ? DOCUSIGN_TOAST_MESSAGES.templateUpdated : DOCUSIGN_TOAST_MESSAGES.templateCreated,
              { fileName: templateName },
            ),
            id: uniqueId('template-create-success'),
            toastType: 'success',
          }),
        )
      } else {
        if (!wasReopeningDraft && templateId) {
          try {
            await deleteTemplate({ templateId, providerId: supervisorProviderId }).unwrap()
          } catch {
            // ignored — cleanup is best-effort
          }
        }
      }
    },
    [deleteTemplate, dispatch, intl, handleUploadModalClose, isReopeningDraft, supervisorProviderId],
  )

  const handleDocuSignCancel = useCallback(async () => {
    const templateId = docuSignDocumentIdRef.current
    const wasReopeningDraft = isReopeningDraft
    setIsDocuSignModalOpen(false)
    setIsReopeningDraft(false)
    handleUploadModalClose()
    if (!wasReopeningDraft && templateId) {
      try {
        await deleteTemplate({ templateId, providerId: supervisorProviderId }).unwrap()
      } catch {
        // best-effort cleanup
      }
    }
  }, [deleteTemplate, handleUploadModalClose, isReopeningDraft, supervisorProviderId])

  const handleDocuSignError = useCallback(
    async (_errorMessage: string) => {
      const templateId = docuSignDocumentIdRef.current
      const templateName = docuSignDocumentNameRef.current
      const wasReopeningDraft = isReopeningDraft
      setIsDocuSignModalOpen(false)
      setIsReopeningDraft(false)
      handleUploadModalClose()
      if (!wasReopeningDraft && templateId) {
        try {
          await deleteTemplate({ templateId, providerId: supervisorProviderId }).unwrap()
        } catch {
          // best-effort cleanup
        }
      }
      dispatch(
        setToastContent({
          text: intl.formatMessage(
            {
              defaultMessage: 'There was an error creating template {fileName}. Please try again.',
              description: 'Toast message after failed DocuSign template creation',
            },
            { fileName: templateName },
          ),
          id: uniqueId('template-create-error'),
          toastType: 'error',
        }),
      )
    },
    [deleteTemplate, dispatch, handleUploadModalClose, intl, isReopeningDraft, supervisorProviderId],
  )

  const handleDownload = useCallback(
    async (doc: LibraryDocumentItem) => {
      try {
        const blob = await downloadTemplate({ templateId: doc.id, providerId: supervisorProviderId }).unwrap()
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = doc.fileName
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(url)
      } catch {
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: 'Download failed. Please try again.',
              description: 'Error message when template download fails',
            }),
            id: uniqueId('template-download-error'),
            toastType: 'error',
          }),
        )
      }
    },
    [dispatch, downloadTemplate, intl, supervisorProviderId],
  )

  const handleViewDocument = useCallback(
    async (doc: LibraryDocumentItem) => {
      try {
        const blob = await downloadTemplate({ templateId: doc.id, providerId: supervisorProviderId }).unwrap()
        if (blob.type.startsWith('image/') || blob.type === 'application/pdf') {
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank', 'noopener,noreferrer')
          setTimeout(() => URL.revokeObjectURL(url), 10000)
        } else {
          await handleDownload(doc)
        }
      } catch {
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: 'Failed to open document. Please try again.',
              description: 'Error message when template view fails',
            }),
            id: uniqueId('template-view-error'),
            toastType: 'error',
          }),
        )
      }
    },
    [dispatch, downloadTemplate, handleDownload, intl, supervisorProviderId],
  )

  const handleRowClick = useCallback(
    (row: Row<LibraryDocumentItem>) => {
      handleViewDocument(row.original)
    },
    [handleViewDocument],
  )

  const handleDeleteClick = useCallback((doc: LibraryDocumentItem) => setDocumentToDelete(doc), [])

  const handleDeleteCancel = useCallback(() => setDocumentToDelete(null), [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!documentToDelete) return
    try {
      await deleteTemplate({ templateId: documentToDelete.id, providerId: supervisorProviderId }).unwrap()
    } catch {
      dispatch(
        setToastContent({
          text: intl.formatMessage({
            defaultMessage: 'Delete failed. Please try again.',
            description: 'Error message when template delete fails',
          }),
          id: uniqueId('template-delete-error'),
          toastType: 'error',
        }),
      )
    }
    setDocumentToDelete(null)
  }, [deleteTemplate, dispatch, documentToDelete, intl, supervisorProviderId])

  const handleEditClick = useCallback(
    (doc: LibraryDocumentItem) => {
      setDocumentToEdit(doc)
      setEditFileName(doc.fileName)
      setEditDocumentType(!isDaProvider && doc.type === DocumentType.INTAKE_FORM ? DocumentType.ASSESSMENT : doc.type)
      setEditAutoSend(doc.autoSend)
    },
    [isDaProvider],
  )

  const handleEditCancel = useCallback(() => setDocumentToEdit(null), [])

  const toggleEditAutoSend = (option: string) => {
    setEditAutoSend((prev) => (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]))
  }

  const handleEditConfirm = useCallback(async () => {
    if (!documentToEdit || !editFileName || !editDocumentType) return
    try {
      await updateTemplate({
        templateId: documentToEdit.id,
        fileName: editFileName,
        documentType: UI_TO_API_DOCUMENT_TYPE[editDocumentType],
        autoSend: editAutoSend,
        providerId: supervisorProviderId,
      }).unwrap()
      setDocumentToEdit(null)
    } catch {
      dispatch(
        setToastContent({
          text: intl.formatMessage({
            defaultMessage: 'Update failed. Please try again.',
            description: 'Error message when template update fails',
          }),
          id: uniqueId('template-edit-error'),
          toastType: 'error',
        }),
      )
    }
    setDocumentToEdit(null)
  }, [
    dispatch,
    documentToEdit,
    editAutoSend,
    editDocumentType,
    editFileName,
    intl,
    supervisorProviderId,
    updateTemplate,
  ])

  const filterSections = useMemo(
    (): FilterSectionConfig<DocumentsPageFilterState>[] => [
      {
        key: 'type',
        label: intl.formatMessage({ defaultMessage: 'Type', description: 'Type filter section' }),
        options: Object.values(DocumentType)
          .filter((type) => isDaProvider || type !== DocumentType.INTAKE_FORM)
          .map((type) => ({ id: type, text: type })),
      },
      ...(isDaProvider
        ? [
            {
              key: 'autoSend' as const,
              label: intl.formatMessage({ defaultMessage: 'Auto Send', description: 'Auto Send filter section' }),
              options: Array.from(new Set(documents.flatMap((doc) => doc.autoSend))).map((val) => {
                const option = AUTO_SEND_OPTIONS.find((o) => o.value === val)
                return { id: val, text: option ? intl.formatMessage(AUTO_SEND_MESSAGES[option.key]) : val }
              }),
            },
          ]
        : []),
    ],
    [documents, intl, isDaProvider],
  )

  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)

  const filteredDocuments = useMemo(() => {
    let filtered = documents

    if (searchTerm.trim()) {
      filtered = filtered.filter((doc) => doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()))
    }

    if (filterState.type.length > 0) {
      filtered = filtered.filter((doc) => filterState.type.includes(doc.type))
    }

    if (filterState.autoSend.length > 0) {
      filtered = filtered.filter((doc) => filterState.autoSend.some((program) => doc.autoSend.includes(program)))
    }

    return filtered
  }, [documents, searchTerm, filterState])

  const getProgramLabel = useCallback(
    (programId: string): string => {
      const option = AUTO_SEND_OPTIONS.find((o) => o.value === programId)
      return option ? intl.formatMessage(AUTO_SEND_MESSAGES[option.key]) : programId
    },
    [intl],
  )

  const columns = useDocumentsColumns(getProgramLabel, isDaProvider)

  const renderRowActions = (row: Row<LibraryDocumentItem>, isHovered: boolean) => {
    if (!isHovered) return null
    const doc = row.original
    const isDraft = !!doc.signingFlow
    return (
      <RowActionsContainer>
        {isDraft && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Open in DocuSign',
              description: 'Tooltip for open draft template in DocuSign button',
            })}
            hoverEnabled
            placement='top'
            onPress={() => handleOpenDraftInDocuSign(doc)}
            testID={tID(`Documents-openDraft-${doc.id}`)}
          >
            <ActionIconContainer>
              <ExternalLinkIcon size={20} strokeColor={colors.iconSuccessMuted} />
            </ActionIconContainer>
          </Tooltip>
        )}
        <Tooltip
          content={intl.formatMessage({
            defaultMessage: 'Download',
            description: 'Tooltip for download document button',
          })}
          hoverEnabled
          placement='top'
          onPress={() => handleDownload(doc)}
          testID={tID(`Documents-download-${doc.id}`)}
        >
          <ActionIconContainer>
            <DownloadFileIcon size={20} fill={colors.iconSuccessMuted} />
          </ActionIconContainer>
        </Tooltip>
        <Tooltip
          content={intl.formatMessage({
            defaultMessage: 'Edit details',
            description: 'Tooltip for edit document button',
          })}
          hoverEnabled
          placement='top'
          onPress={() => handleEditClick(doc)}
          testID={tID(`Documents-edit-${doc.id}`)}
        >
          <ActionIconContainer>
            <EditIcon size={20} fillColor={colors.iconSuccessMuted} isFilled={false} />
          </ActionIconContainer>
        </Tooltip>
        <Tooltip
          content={intl.formatMessage({ defaultMessage: 'Delete', description: 'Tooltip for delete document button' })}
          hoverEnabled
          placement='top'
          onPress={() => handleDeleteClick(doc)}
          testID={tID(`Documents-delete-${doc.id}`)}
        >
          <ActionIconContainer>
            <TrashIcon size={20} fillColor={colors.iconSuccessMuted} />
          </ActionIconContainer>
        </Tooltip>
      </RowActionsContainer>
    )
  }

  return (
    <OuterContainer>
      <Container testID={tID('Documents-container')}>
        <Title
          size={TitleSize.LARGE}
          text={<FormattedMessage defaultMessage='Document library' description='Document library title' />}
        />
        <FilterRowWrapper>
          <FilterRow>
            <InputField
              testID={tID('Documents-search-input')}
              accessibilityLabel={intl.formatMessage({
                defaultMessage: 'Search documents by file name',
                description: 'Accessibility label for document search input',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Search file name',
                description: 'Search documents by file name',
              })}
              baseInputStyle={{ width: 300, marginBottom: 0 }}
              inputContainerStyle={{ borderRadius: 8, borderColor: colors.borderDefault }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.nativeEvent.text)}
              leftIcon={
                <SearchIconWrapper>
                  <SearchIcon fillColor={colors.iconDefault} />
                </SearchIconWrapper>
              }
              inputExtras={
                searchTerm ? (
                  <UnstyledIconButton
                    testID={tID('Documents-clear-button')}
                    onPress={() => setSearchTerm('')}
                    accessibilityLabel={intl.formatMessage({
                      defaultMessage: 'Clear search',
                      description: 'Clear search button',
                    })}
                  >
                    <XIcon fillColor={colors.iconDefault} />
                  </UnstyledIconButton>
                ) : undefined
              }
            />
            <DocumentFilters
              filterState={filterState}
              initialFilterState={INITIAL_DOCUMENTS_PAGE_FILTER_STATE}
              onFilterStateChange={setFilterState}
              sections={filterSections}
            />
          </FilterRow>
          {filteredDocuments.length > 0 && (
            <PrimaryButton
              text={<FormattedMessage defaultMessage='Upload' description='Upload button text' />}
              onPress={handleUploadModalOpen}
              testID={tID('Documents-upload-button')}
            />
          )}
        </FilterRowWrapper>
        {isTemplatesLoading ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 64 }}>
            <ActivityIndicator testID={tID('Documents-loading')} />
          </View>
        ) : (
          <DataTable
            columns={columns}
            data={filteredDocuments}
            pagination={filteredDocuments.length > 25 ? { pageSize: 25 } : undefined}
            rowHeight={72}
            cellAlignment='left'
            rowBorder
            striped={false}
            withRowCount
            withScroll
            sortingState={sortingState}
            onSortingChange={setSortingState}
            hoveredRowId={hoveredRowId}
            onRowHover={setHoveredRowId}
            onRowClick={handleRowClick}
            rowActions={renderRowActions}
            pagnationStyle={{ boxShadow: `0 -4px 16px ${colors.shadowLow}` }}
            emptyTableComponent={
              <EmptyTableContainer>
                <EmptyTableText>
                  {intl.formatMessage({
                    defaultMessage: 'Document Library is empty',
                    description: 'Empty state title',
                  })}
                </EmptyTableText>
                <EmptyTableText color={colors.textSecondary}>
                  {intl.formatMessage({
                    defaultMessage: 'Upload reusable documents to share with clients',
                    description: 'Empty state description',
                  })}
                </EmptyTableText>
                <EmptyTableButtonWrapper>
                  <PrimaryButton
                    text={<FormattedMessage defaultMessage='Upload' description='Upload button text' />}
                    onPress={handleUploadModalOpen}
                    testID={tID('Documents-upload-button')}
                  />
                </EmptyTableButtonWrapper>
              </EmptyTableContainer>
            }
          />
        )}
      </Container>
      <FullScreenOverlay isOpen={isUploadModalOpen} isFocused={false} renderInPortal>
        <UploadModalContainer testID={tID('Documents-uploadModal')}>
          <UploadModalHeader>
            <Title
              size={TitleSize.MEDIUM}
              text={<FormattedMessage defaultMessage='Upload to document library' description='Upload modal title' />}
            />
            <UploadModalHeaderButtons>
              <TertiaryButton
                text={<FormattedMessage defaultMessage='Exit' description='Exit button text for upload modal' />}
                onPress={handleUploadModalClose}
                testID={tID('Documents-uploadModal-exit')}
                customTextColor={colors.linkDefault}
              />
              <PrimaryButton
                text={
                  uploadSignatureRequired && uploadSignatureRequired !== 'none' ? (
                    <FormattedMessage
                      defaultMessage='Next'
                      description='Next button text for upload modal when signature is required'
                    />
                  ) : (
                    <FormattedMessage defaultMessage='Upload' description='Upload button text for upload modal' />
                  )
                }
                onPress={handleUploadConfirm}
                testID={tID('Documents-uploadModal-upload')}
                disabled={!uploadFile || !uploadFileName || !uploadDocumentType || isUploading}
                loading={isUploading}
              />
            </UploadModalHeaderButtons>
          </UploadModalHeader>
          <UploadModalContent>
            <UploadTabContent testID={tID('Documents-uploadModal-fromComputer')}>
              <UploadTabSplitContainer>
                <UploadTabLeftPane>
                  <FileUpload
                    name='uploadFile'
                    label={intl.formatMessage({
                      defaultMessage: 'Upload file',
                      description: 'File upload label',
                    })}
                    maxFileSize={DOCUMENT_UPLOAD_MAX_FILE_SIZE}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Click to upload or drag file here',
                      description: 'File upload placeholder text',
                    })}
                    subText={intl.formatMessage({
                      defaultMessage: 'Supported formats: JPEG, PNG, PDF, and DOCX. Max file size: 10 MB',
                      description: 'File upload supported formats and size info',
                    })}
                    onFileSelect={(file) => {
                      setUploadFile(file)
                      if (!file) {
                        setUploadFileName('')
                      } else if (!uploadFileName) {
                        setUploadFileName(file.name)
                      }
                    }}
                    selectedFile={uploadFile}
                    baseInputStyle={{ marginBottom: 20 }}
                    showPreview={false}
                    acceptedFileTypes={[
                      'image/jpeg',
                      'image/png',
                      'application/pdf',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    ]}
                  />
                  <InputField
                    name='fileName'
                    label={intl.formatMessage({
                      defaultMessage: 'File name*',
                      description: 'File name input label',
                    })}
                    value={uploadFileName}
                    onChange={(e) => {
                      setUploadFileName(e.nativeEvent.text)
                      setUploadErrorMessage(undefined)
                    }}
                    error={uploadErrorMessage ?? undefined}
                    toolTipContent={intl.formatMessage({
                      defaultMessage: 'File name will be visible to clients if document is shared',
                      description: 'File name tooltip',
                    })}
                    toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                    toolTipHoverEnabled
                    baseInputStyle={{ marginBottom: 20 }}
                    maxCharLength={DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH}
                  />
                  <SelectField
                    name='documentType'
                    label={intl.formatMessage({
                      defaultMessage: 'Type*',
                      description: 'Document type dropdown label',
                    })}
                    value={uploadDocumentType}
                    onChange={(value) => setUploadDocumentType(value)}
                    options={documentTypeOptions}
                    baseInputStyle={{ marginBottom: 20 }}
                    customWebStyle={!uploadDocumentType ? { color: colors.textInactive } : undefined}
                  />
                  <SelectField
                    name='signatureRequired'
                    label={intl.formatMessage({
                      defaultMessage: 'Signature required',
                      description: 'Signature required dropdown label',
                    })}
                    value={uploadSignatureRequired}
                    onChange={(value) => setUploadSignatureRequired(value)}
                    options={signatureRequiredOptions}
                    toolTipContent={intl.formatMessage({
                      defaultMessage: 'Signature collected by DocuSign. Set up signature location in next step',
                      description: 'Signature required tooltip',
                    })}
                    toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                    toolTipHoverEnabled
                    baseInputStyle={{ marginBottom: 20 }}
                    customWebStyle={!uploadSignatureRequired ? { color: colors.textInactive } : undefined}
                  />
                  {isDaProvider && (
                    <AutoSendSection>
                      <AutoSendHeaderBlock>
                        <AutoSendHeaderRow>
                          <BodyText
                            text={intl.formatMessage({
                              defaultMessage: 'Auto Send Settings',
                              description: 'Auto Send settings section label',
                            })}
                            size={BodyTextSize.DEFAULT}
                          />
                          <Tooltip
                            content={intl.formatMessage({
                              defaultMessage:
                                'Only the client who booked the session will automatically receive this document.',
                              description: 'Auto Send settings tooltip',
                            })}
                            triggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                            hoverEnabled
                            placement='right'
                          />
                        </AutoSendHeaderRow>
                        <BodyText
                          text={intl.formatMessage({
                            defaultMessage:
                              'For selected services, clients will automatically receive this document after booking an intake session.',
                            description: 'Auto Send settings description',
                          })}
                          size={BodyTextSize.SMALL}
                          color={colors.textSecondary}
                        />
                      </AutoSendHeaderBlock>
                      {AUTO_SEND_OPTIONS.map((option) => (
                        <Checkbox
                          key={option.value}
                          name={`autoSend-${option.value}`}
                          title={intl.formatMessage(AUTO_SEND_MESSAGES[option.key])}
                          checked={autoSendSettings.includes(option.value)}
                          onPress={() => toggleAutoSend(option.value)}
                          baseInputStyle={{ marginBottom: 0 }}
                          testID={tID(`Documents-uploadModal-autoSend-${option.value}`)}
                        />
                      ))}
                    </AutoSendSection>
                  )}
                </UploadTabLeftPane>
                <UploadTabRightPane>
                  <FilePreviewContainer>
                    {uploadFile && filePreviewUrl ? (
                      isImageFile(uploadFile) ? (
                        <Image
                          source={{ uri: filePreviewUrl }}
                          alt={intl.formatMessage({
                            defaultMessage: 'File preview',
                            description: 'Alt text for file preview image',
                          })}
                          contentFit='contain'
                          style={{ width: '100%', height: '100%' }}
                          accessibilityIgnoresInvertColors
                          testID={tID('upload-file-preview-image')}
                        />
                      ) : isPdfFile(uploadFile) ? (
                        <iframe
                          src={filePreviewUrl}
                          title={intl.formatMessage({
                            defaultMessage: 'PDF preview',
                            description: 'Title for PDF preview iframe',
                          })}
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          data-testid={tID('upload-file-preview-pdf')}
                        />
                      ) : (
                        <FilePreviewEmptyState>
                          <BodyText
                            text={uploadFile.name}
                            size={BodyTextSize.DEFAULT}
                            color={colors.textSecondary}
                            textAlign='center'
                          />
                        </FilePreviewEmptyState>
                      )
                    ) : (
                      <FilePreviewEmptyState>
                        <BodyText
                          text={intl.formatMessage({
                            defaultMessage: 'Document preview here',
                            description: 'Placeholder text when no file is selected',
                          })}
                          size={BodyTextSize.DEFAULT}
                          color={colors.textSecondary}
                          textAlign='center'
                        />
                      </FilePreviewEmptyState>
                    )}
                  </FilePreviewContainer>
                </UploadTabRightPane>
              </UploadTabSplitContainer>
            </UploadTabContent>
          </UploadModalContent>
        </UploadModalContainer>
      </FullScreenOverlay>
      <ConfirmationModal
        visible={!!documentToDelete}
        modalTitle={intl.formatMessage({
          defaultMessage: 'Delete document?',
          description: 'Delete confirmation modal title',
        })}
        modalContents={intl.formatMessage(
          {
            defaultMessage: 'Are you sure you want to delete "{fileName}"? This action cannot be undone.',
            description: 'Delete confirmation modal body',
          },
          { fileName: documentToDelete?.fileName ?? '' },
        )}
        confirmationButtonText={intl.formatMessage({
          defaultMessage: 'Delete',
          description: 'Delete confirmation button',
        })}
        cancelButtonText={intl.formatMessage({
          defaultMessage: 'Cancel',
          description: 'Cancel button',
        })}
        onConfirmationButtonPress={handleDeleteConfirm}
        onRequestClose={handleDeleteCancel}
        confirmButtonLoading={isDeleting}
        testID={tID('Documents-deleteModal')}
      />
      <Modal
        visible={!!documentToEdit}
        modalTitle={intl.formatMessage({
          defaultMessage: 'Edit document details',
          description: 'Edit document modal title',
        })}
        onRequestClose={handleEditCancel}
        modalContents={
          <EditModalContent testID={tID('Documents-editModal')}>
            <EditModalHeader>
              <Title
                size={TitleSize.MEDIUM}
                text={
                  <FormattedMessage defaultMessage='Edit document details' description='Edit document modal title' />
                }
              />
            </EditModalHeader>
            <InputField
              name='editFileName'
              label={intl.formatMessage({
                defaultMessage: 'File name*',
                description: 'Edit file name input label',
              })}
              value={editFileName}
              onChange={(e) => setEditFileName(e.nativeEvent.text)}
              baseInputStyle={{ marginBottom: 0 }}
              maxCharLength={DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH}
              toolTipContent={intl.formatMessage({
                defaultMessage: 'File name will be visible to clients if document is shared',
                description: 'Tooltip for edit document file name',
              })}
              toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
              toolTipHoverEnabled
            />
            <SelectField
              name='editDocumentType'
              label={intl.formatMessage({
                defaultMessage: 'Type*',
                description: 'Edit document type dropdown label',
              })}
              value={editDocumentType}
              onChange={(value) => setEditDocumentType(value as DocumentType)}
              options={documentTypeOptions}
              baseInputStyle={{ marginBottom: 0 }}
            />
            {isDaProvider && (
              <AutoSendSection>
                <AutoSendHeaderBlock>
                  <AutoSendHeaderRow>
                    <BodyText
                      text={intl.formatMessage({
                        defaultMessage: 'Auto Send Settings',
                        description: 'Auto Send settings section label',
                      })}
                      size={BodyTextSize.DEFAULT}
                    />
                    <Tooltip
                      content={intl.formatMessage({
                        defaultMessage:
                          'Only the client who booked the session will automatically receive this document.',
                        description: 'Auto Send settings tooltip',
                      })}
                      triggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                      hoverEnabled
                      placement='right'
                    />
                  </AutoSendHeaderRow>
                  <BodyText
                    text={intl.formatMessage({
                      defaultMessage:
                        'For selected services, clients will automatically receive this document after booking an intake session.',
                      description: 'Auto send description',
                    })}
                    size={BodyTextSize.SMALL}
                    color={colors.textSecondary}
                  />
                </AutoSendHeaderBlock>
                {AUTO_SEND_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.value}
                    name={`editAutoSend-${option.value}`}
                    title={intl.formatMessage(AUTO_SEND_MESSAGES[option.key])}
                    checked={editAutoSend.includes(option.value)}
                    onPress={() => toggleEditAutoSend(option.value)}
                    baseInputStyle={{ marginBottom: 0 }}
                    testID={tID(`Documents-editModal-autoSend-${option.value}`)}
                  />
                ))}
              </AutoSendSection>
            )}
            <EditModalButtonRow>
              <SecondaryButton
                text={intl.formatMessage({
                  defaultMessage: 'Cancel',
                  description: 'Cancel button',
                })}
                onPress={handleEditCancel}
                testID={tID('Documents-editModal-cancel')}
              />
              <PrimaryButton
                text={intl.formatMessage({
                  defaultMessage: 'Save',
                  description: 'Save button in edit modal',
                })}
                onPress={handleEditConfirm}
                testID={tID('Documents-editModal-save')}
                disabled={!editFileName || !editDocumentType || isUpdating}
                loading={isUpdating}
              />
            </EditModalButtonRow>
          </EditModalContent>
        }
      />
      <FullScreenOverlay isOpen={isDocuSignModalOpen} isFocused={false} renderInPortal>
        <DocuSignEmbeddedView
          signingUrl={docuSignUrl}
          title={intl.formatMessage({
            defaultMessage: 'Set up signature locations',
            description: 'DocuSign embedded view header when setting up a document for signing',
          })}
          onComplete={handleDocuSignComplete}
          onCancel={handleDocuSignCancel}
          onError={handleDocuSignError}
        />
      </FullScreenOverlay>
    </OuterContainer>
  )
}

export default DocumentsPage
